/* eslint-disable max-lines-per-function */
import {
  useFloating,
  useClick,
  useInteractions,
  useDismiss,
  FloatingPortal,
  useRole,
  FloatingArrow,
  arrow,
  offset,
  autoUpdate,
  autoPlacement,
  useHover,
  useTransitionStyles,
  FloatingTree,
  useFloatingParentNodeId,
  FloatingNode,
  useFloatingNodeId,
} from '@floating-ui/react';
import React, { useMemo, useRef, useState } from 'react';

import type { PopoverProps } from './popover.types';
import { portalElement } from '../..';

export type PopoverPlacement = 'bottomLeft' | 'bottomRight' | 'topLeft' | 'topRight';

export interface PopoverPosition {
  top?: number;
  bottom?: number;
  left?: number;
  right?: number;
  placement?: PopoverPlacement;
}

interface PopoverComponentProps extends PopoverProps {
  parentId?: string;
}

const options = {
  popover: {
    wrapper: 'divide-y rounded-lg divide-gray-300 shadow-dark bg-white border-white origin-top-right p-0 z-50',
    content: 'rounded-lg border-none overflow-hidden',
    title: 'text-sm font-bold border-b py-1 px-4 leading-6',
    innerContent: 'text-sm py-3 px-4 min-w-[177px] empty:p-0',
  },
};

const cssConfig = {
  svgArrowWidthInPx: 14,
  remInPx: 16,
  borderRadiusInRem: 0.5, // set in 'rounded-lg' class
};

function PopoverComponent(props: PopoverComponentProps) {
  const {
    title = '',
    content = '',
    trigger = 'click',
    children = <></>,
    placement = 'bottom-start',
    autoPlacementOptions,
    open: controlledOpen,
    showArrow = true,
    arrowFill = 'white',
    overlayStyle = {} as React.CSSProperties,
    overlayClassName = '',
    innerContentClassName,
    overlayInnerStyle,
    offsetOptions = { mainAxis: cssConfig.svgArrowWidthInPx / 2 },
    onOpenChange: setControlledOpen,
    transitionDuration = 0,
    role = 'dialog',
    containerClassName,
    parentId,
    enabled = true,
    isDismissBubbleEnabled = false,
  } = props;

  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);

  const arrowRef = useRef(null);
  const nodeId = useFloatingNodeId(parentId);

  const open = useMemo(() => controlledOpen ?? uncontrolledOpen, [controlledOpen, uncontrolledOpen]);
  const setOpen = setControlledOpen ?? setUncontrolledOpen;

  const middleware = [
    arrow({
      element: arrowRef,
      padding: cssConfig.remInPx * cssConfig.borderRadiusInRem,
    }),
    offset(offsetOptions),
  ];

  if (autoPlacementOptions) middleware.push(autoPlacement(autoPlacementOptions));

  const { refs, floatingStyles, context } = useFloating({
    nodeId,
    placement,
    open,
    onOpenChange: setOpen,
    whileElementsMounted: autoUpdate,
    middleware,
  });

  const clickEnabled = trigger.includes('click') && enabled;
  const hoverEnabled = trigger.includes('hover') && enabled;

  const click = useClick(context, { enabled: clickEnabled, toggle: false });
  const hover = useHover(context, {
    enabled: hoverEnabled,
    move: false,
    delay: {
      open: 0,
      close: 100,
    },
  });

  const dismiss = useDismiss(context, {
    bubbles: isDismissBubbleEnabled,
    referencePress: false,
  });

  const roleInteraction = useRole(context, { role });
  const { getReferenceProps, getFloatingProps } = useInteractions([click, hover, dismiss, roleInteraction]);
  const { isMounted, styles } = useTransitionStyles(context, { duration: transitionDuration });

  const className = `${overlayClassName} ${options.popover.wrapper}`;

  return (
    <>
      <div ref={refs.setReference} className={`popover inline-flex ${containerClassName}`} {...getReferenceProps()}>
        {children}
      </div>
      <FloatingNode id={nodeId}>
        {open && isMounted && (
          <FloatingPortal root={portalElement() as HTMLElement}>
            <div
              className={className}
              ref={refs.setFloating}
              style={{ ...floatingStyles, ...overlayStyle, ...styles }}
              {...getFloatingProps()}
            >
              {showArrow && <FloatingArrow ref={arrowRef} context={context} fill={arrowFill} className="z-50" />}
              <div
                className="overflow-hidden border-none"
                style={
                  // when arrow present in top and bottom placements, prevent it from overhanging border radius
                  // also includes an anti-nullish-coalescing-operator check on innerContentClassName,
                  //      due to default popover.innerContent classes
                  !(innerContentClassName === null || innerContentClassName === undefined) &&
                  showArrow &&
                  (placement.startsWith('top') || placement.startsWith('bottom'))
                    ? {
                        minWidth: `${
                          cssConfig.svgArrowWidthInPx + 2 * cssConfig.borderRadiusInRem * cssConfig.remInPx
                        }px`,
                        textAlign: 'center',
                      }
                    : {}
                }
              >
                {title && <div className={options.popover.title}>{title}</div>}
                <div style={overlayInnerStyle} className={innerContentClassName ?? options.popover.innerContent}>
                  {content}
                </div>
              </div>
            </div>
          </FloatingPortal>
        )}
      </FloatingNode>
    </>
  );
}

export function Popover(props: PopoverProps) {
  const parentId = useFloatingParentNodeId();

  if (parentId === null) {
    return (
      <FloatingTree>
        <PopoverComponent {...{ ...props, parentId: parentId ?? undefined }} />
      </FloatingTree>
    );
  }

  return <PopoverComponent {...props} />;
}

export default Popover;
